import React, { useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/api/client";
import { useLang } from "@/context/LanguageContext";
import { trackCall } from "@/lib/track";

const WA_NUMBER = "13316459072";
const MESSENGER_URL = "https://m.me/FloresOnTheMoveUS";

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.15-.174.2-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.687 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const MessengerIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.975 12-11.111C24 4.975 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8.1l3.13 3.259L19.752 8.1l-6.561 6.863z" />
  </svg>
);

export default function FloatingActions() {
  const { lang } = useLang();
  const [chatOpen, setChatOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error(lang === "es" ? "Nombre y mensaje son requeridos" : "Name and message are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name,
        phone: form.contact,
        email: form.contact.includes("@") ? form.contact : "",
        message: form.message,
        source: "chat_widget",
      });
      toast.success(lang === "es" ? "¡Enviado! Te contactamos pronto." : "Sent! We'll reach out shortly.");
      setForm({ name: "", contact: "", message: "" });
      setChatOpen(false);
    } catch (err) {
      toast.error(lang === "es" ? "Error al enviar" : "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  const t = lang === "es"
    ? { chat: "Chat directo", wa: "WhatsApp", msg: "Messenger", title: "Chatea con nosotros", sub: "Te respondemos rápido.", name: "Tu nombre", contact: "Teléfono o email", msgLabel: "Tu mensaje", send: "Enviar" }
    : { chat: "Live chat", wa: "WhatsApp", msg: "Messenger", title: "Chat with us", sub: "We'll respond fast.", name: "Your name", contact: "Phone or email", msgLabel: "Your message", send: "Send" };

  return (
    <>
      {/* Cluster - hidden inside portal */}
      <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3" data-testid="floating-actions">
        {expanded && (
          <>
            <a
              href={`https://wa.me/${WA_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCall("fab_whatsapp")}
              aria-label="WhatsApp"
              className="group flex items-center gap-3 pl-4 pr-3 py-2 rounded-full bg-[#25D366] text-white shadow-lg hover:shadow-xl transition-transform hover:-translate-y-0.5"
              data-testid="fab-whatsapp"
            >
              <span className="text-sm font-semibold hidden sm:block">{t.wa}</span>
              <WhatsAppIcon className="h-6 w-6" />
            </a>
            <a
              href={MESSENGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCall("fab_messenger")}
              aria-label="Messenger"
              className="group flex items-center gap-3 pl-4 pr-3 py-2 rounded-full bg-[#0084FF] text-white shadow-lg hover:shadow-xl transition-transform hover:-translate-y-0.5"
              data-testid="fab-messenger"
            >
              <span className="text-sm font-semibold hidden sm:block">{t.msg}</span>
              <MessengerIcon className="h-6 w-6" />
            </a>
            <button
              onClick={() => { setChatOpen(true); setExpanded(false); }}
              aria-label="Live chat"
              className="group flex items-center gap-3 pl-4 pr-3 py-2 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-transform hover:-translate-y-0.5"
              data-testid="fab-chat"
            >
              <span className="text-sm font-semibold hidden sm:block">{t.chat}</span>
              <MessageCircle className="h-6 w-6" />
            </button>
          </>
        )}
        <button
          onClick={() => setExpanded(!expanded)}
          aria-label="Contact options"
          className={`h-14 w-14 rounded-full shadow-2xl flex items-center justify-center text-white transition-transform hover:-translate-y-0.5 ${expanded ? "bg-neutral-800 rotate-45" : "bg-primary"}`}
          data-testid="fab-toggle"
        >
          {expanded ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        </button>
      </div>

      {/* Live chat panel */}
      {chatOpen && (
        <div className="fixed bottom-24 right-5 z-50 w-[min(92vw,360px)] rounded-2xl border border-border bg-card shadow-2xl overflow-hidden" data-testid="chat-panel">
          <div className="p-4 flex items-center justify-between" style={{ backgroundColor: "#2E3944" }}>
            <div>
              <div className="font-semibold text-white" style={{ fontFamily: "Outfit" }}>{t.title}</div>
              <div className="text-xs text-neutral-300">{t.sub}</div>
            </div>
            <button onClick={() => setChatOpen(false)} className="text-neutral-400 hover:text-white" aria-label="Close chat" data-testid="chat-close-btn">
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3">
            <Input placeholder={t.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background" data-testid="chat-name-input" />
            <Input placeholder={t.contact} value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="bg-background" data-testid="chat-contact-input" />
            <Textarea placeholder={t.msgLabel} rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-background" data-testid="chat-message-input" />
            <Button type="submit" disabled={submitting} className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="chat-send-btn">
              {submitting ? "..." : t.send}
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
